import { useEffect, useRef, useState } from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridFilterModel } from "@mui/x-data-grid";
import { useLocation } from "react-router-dom";
import CallButton from "../Common/CallButton/CallButton";
import { UserType } from "../../enums/common";
import { formatDate } from "../../utils/DateTimeUtils";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";

const OneMealListForModule = (props: any) => {
  let location = useLocation();
  let oneMealServiceId = location.pathname.split("/")[2];

  const [page, setPage] = useState<number>(props.initialPage ?? 0);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    props.initialFilterModel ?? { items: [] }
  );
  const scrolledRef = useRef(false);
  const gridWrapperRef = useRef<HTMLDivElement | null>(null);

  const saveGridState = (state: any) => {
    if (!props.storageKey) return;
    try {
      const savedState = JSON.parse(sessionStorage.getItem(props.storageKey) || '{}');
      sessionStorage.setItem(
        props.storageKey,
        JSON.stringify({
          ...savedState,
          page,
          pageSize: props.pageSize,
          filterModel,
          ...state,
        })
      );
    } catch {
      // sessionStorage not available
    }
  };

  // Scroll the last opened row into view once data loads.
  useEffect(() => {
    if (!props.data?.length || scrolledRef.current) return;
    const targetId = props.clickedId ?? Number(oneMealServiceId);
    if (!targetId) return;

    // DataGrid renders rows with data-id attribute
    const tryScroll = (attempts: number) => {
      const rowIndex = props.data.findIndex((row: any) => Number(row?.id) === Number(targetId));
      if (rowIndex >= 0) {
        const virtualScroller = gridWrapperRef.current?.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null;
        if (virtualScroller) {
          virtualScroller.scrollTop = rowIndex * 52;
        }
      }

      const row = gridWrapperRef.current?.querySelector(`[data-id="${targetId}"]`) as HTMLElement | null;
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrolledRef.current = true;
      } else if (attempts > 0) {
        setTimeout(() => tryScroll(attempts - 1), 100);
      }
    };
    tryScroll(10);
  }, [props.data, props.clickedId, oneMealServiceId]);

  const handleShowClick = (rowId: number | string) => {
    if (props.setClickedId) {
      props.setClickedId(Number(rowId));
    }
    saveGridState({ clickedId: Number(rowId) });
  };

  const columns: GridColDef[] = !props.party ? [
    { field: "id", headerName: "ID"},
    {
      field: "customerName",
      headerName: "Customer Name",
      minWidth: 130,
    },
    {
      field: "customerAddress",
      headerName: "Customer Address",
      minWidth: 180,
      renderCell: (cellValues) => {
        return (
          <div>
            {[cellValues?.row?.localityName, cellValues?.row?.areaName, cellValues?.row?.cityName].filter(Boolean).join(", ") || "Not Available"}
          </div>
        );
      },
    },
    {
      field: "cookId",
      headerName: "Cook Id",
      minWidth: 80,
      renderCell: (cellValues) => {
        if (!cellValues?.row?.cookId) return "---";
        return <div>{`${cellValues?.row?.cookId}`}</div>;
      },
    },
    {
      field: "cookName",
      headerName: "Cook Name",
      minWidth: 120,
      renderCell: (cellValues) => {
        if (!cellValues?.row?.cookName) return "---";
        return (
          <div>
            {`${cellValues?.row?.cookName}`}
          </div>
        );
      },
    },
    {
      field: "cookMobile",
      headerName: "Cook Mobile",
      renderCell: (cellValues) => {
        if (!cellValues?.row?.cookPhoneNumber) return "---";
        return <CallButton toPhoneNumber={cellValues?.row?.cookPhoneNumber} userType={UserType.COOK} referenceId={cellValues?.row?.id} referenceModelName={"ONE_MEAL_SERVICE"}/>
      },
      minWidth: 120,
    },
    {
      field: "date",
      headerName: "Date",
      sortable: true,
      minWidth: 120,
      renderCell: (cellValues) => formatDate(cellValues?.row?.date),
    },
    { field: "time", headerName: "Time" },
    { field: "status", headerName: "Status", minWidth: 150 },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      minWidth: 150,
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (cellValues) => {
        if (Number(cellValues?.id) !== Number(oneMealServiceId)) {
          return (
            <Link
              href={`/#/OneMealService/${cellValues?.id}/show`}
              onClick={() => handleShowClick(cellValues?.id)}
            >
              <Button variant={"contained"} color={"primary"}>
                Show
              </Button>
            </Link>
          );
        }
        return null;
      },
    },
  ] : [
    { field: "id", headerName: "ID", flex: 0.3},
    {
      field: "customerName",
      headerName: "Customer Name",
    },
    {
      field: "customerAddress",
      headerName: "Customer Address",
      renderCell: (cellValues) => {
        return (
          <div>
            {`${cellValues?.row?.house?.address} ${cellValues?.row?.house?.locality} ${cellValues?.row?.house?.city}`}
          </div>
        );
      },
    },
    {
      field: "cookId",
      headerName: "Cook Id",
      renderCell: (cellValues) => {
        let currentCook = cellValues?.row?.cooks?.filter((cook: any) => cook?.type === "MAIN_CHEF")[0];
        if (!currentCook) return "---";
        return <div>{`${currentCook?.details?.cookId}`}</div>;
      },
    },
    {
      field: "cookName",
      headerName: "Cook Name",
      renderCell: (cellValues) => {
        let currentCook = cellValues?.row?.cooks?.filter((cook: any) => cook?.type === "MAIN_CHEF")[0];
        if (!currentCook) return "---";
        return <div>{`${currentCook?.details?.cookName}`}</div>;
      },
    },
    {
      field: "cookMobile",
      headerName: "Cook Mobile",
      renderCell: (cellValues) => {
        let currentCook = cellValues?.row?.cooks?.filter((cook: any) => cook?.type === "MAIN_CHEF")[0];
        if (!currentCook) return "---";
        return <div>{maskPhoneNumber(currentCook?.details?.cookPhone)}</div>;
      },
      minWidth: 120,
    },
    {
      field: "date",
      headerName: "Date",
      sortable: true,
      minWidth: 120,
      renderCell: (cellValues) => formatDate(cellValues?.row?.date),
    },
    { field: "time", headerName: "Time" },
    { field: "status", headerName: "Status" },
    {
      field: "action",
      headerName: "Action",
      renderCell: (cellValues) => {
        if (Number(cellValues?.id) !== Number(oneMealServiceId)) {
          return (
            <Link
              href={!props?.party ? `/#/OneMealService/${cellValues?.id}/show` : `/#/ChefForParty/${cellValues?.id}/show`}
              onClick={() => handleShowClick(cellValues?.id)}
            >
              <Button variant={"contained"} color={"primary"}>
                Show
              </Button>
            </Link>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div ref={gridWrapperRef} style={{ height: '100vh', width: "100%", marginBottom: "40px" }}>
      {props.storageKey && props.clickedId && (
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "3px",
              backgroundColor: "rgba(255, 167, 38, 0.25)",
              border: "1px solid rgba(245, 124, 0, 0.7)",
            }}
          />
          <Typography variant="body2" fontWeight={600}>
            Last visited
          </Typography>
        </Box>
      )}
      <DataGrid
        columns={columns}
        rows={props?.data ?? []}
        page={page}
        pageSize={props?.pageSize}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => {
          setFilterModel(newFilterModel);
          saveGridState({ filterModel: newFilterModel });
        }}
        onPageSizeChange={(newPageSize) => {
          props.setPageSize(newPageSize);
          setPage(0);
          saveGridState({ page: 0, pageSize: newPageSize });
        }}
        loading={props?.isLoading}
        rowsPerPageOptions={[25, 50, 100]}
        rowCount={props?.count ?? 0}
        paginationMode="server"
        onPageChange={(newPage) => {
          setPage(newPage);
          saveGridState({ page: newPage });
          props.refreshData(newPage);
        }}
        disableSelectionOnClick
        disableColumnFilter={!props.storageKey}
        disableColumnMenu={!props.storageKey}
        experimentalFeatures={{ newEditingApi: true }}
        getRowClassName={(params) => {
          if (props.clickedId && Number(params.id) === Number(props.clickedId)) return 'row-clicked';
          return '';
        }}
        sx={{
          '& .row-clicked': {
            backgroundColor: 'rgba(255, 167, 38, 0.15)',
            '&:hover': { backgroundColor: 'rgba(255, 167, 38, 0.25)' },
          },
        }}
      />
    </div>
  );
};

export default OneMealListForModule;
